import { NfcCard, Visitor } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { toLower } from 'lodash';
import { useCallback, useMemo } from 'react';
import { parseEnumParam, parseParamToBooleanRecord, serializeEnum } from 'v2/filters/filterUrlParams';
import { hasEnabledFilter } from 'v2/filters/hasEnabledFilter';
import { createUserFilterChips } from 'v2/filters/users/createUserFilterChips';
import { createUserFilterSections } from 'v2/filters/users/createUserFilterSections';
import { filterVisitors } from 'v2/filters/users/filterVisitors';
import { filterAccessDefault, filterOriginDefault, filterRoleDefault } from 'v2/filters/users/userFilterConstants';
import {
  FilterAccess,
  FilterOrigin,
  FilterRole,
  FilterUserExpires,
  FilterUserExpiresState,
  UsersFilterState
} from 'v2/filters/users/userFilterTypes';
import { useFilterSearchParams } from 'v2/hooks/useFilterSearchParams/useFilterSearchParams';
import { useVisitorSearch } from 'v2/hooks/useSearch/useVisitorSearch';

interface Params {
  allVisitors: Visitor[];
  allVisitorsCards: NfcCard[];
}

type UserFilterParam = 'search' | 'access' | 'expires' | 'role' | 'origin';

const USER_FILTER_PARAM_KEYS: UserFilterParam[] = ['search', 'access', 'expires', 'role', 'origin'];

const FILTER_USER_EXPIRES_VALUES = Object.values(FilterUserExpires);

export const useUsersSearchAndFilters = ({ allVisitors, allVisitorsCards }: Params) => {
  const { translation } = useTranslation();

  const { searchParams, updateFilterParams, updateMultiSelectFilter } =
    useFilterSearchParams<UserFilterParam>(USER_FILTER_PARAM_KEYS);

  const searchParam = searchParams.get('search');
  const accessParam = searchParams.get('access');
  const expiresParam = searchParams.get('expires');
  const roleParam = searchParams.get('role');
  const originParam = searchParams.get('origin');

  const filterStr = searchParam ?? '';
  const filterAccess = useMemo(() => parseParamToBooleanRecord(accessParam, filterAccessDefault), [accessParam]);
  const filterUserExpires = useMemo(() => parseEnumParam(expiresParam, FILTER_USER_EXPIRES_VALUES), [expiresParam]);
  const filterRole = useMemo(() => parseParamToBooleanRecord(roleParam, filterRoleDefault), [roleParam]);
  const filterOrigin = useMemo(() => parseParamToBooleanRecord(originParam, filterOriginDefault), [originParam]);

  const setFilterStr = useCallback(
    (value: string) => updateFilterParams(() => ({ search: value === '' ? undefined : value })),
    [updateFilterParams]
  );

  const setFilterAccess = useCallback(
    (value: FilterAccess | ((current: FilterAccess) => FilterAccess)) =>
      updateMultiSelectFilter('access', filterAccessDefault, value),
    [updateMultiSelectFilter]
  );

  const setFilterUserExpires = useCallback(
    (value: FilterUserExpiresState) => updateFilterParams(() => ({ expires: serializeEnum(value) })),
    [updateFilterParams]
  );

  const setFilterRole = useCallback(
    (value: FilterRole | ((current: FilterRole) => FilterRole)) =>
      updateMultiSelectFilter('role', filterRoleDefault, value),
    [updateMultiSelectFilter]
  );

  const setFilterOrigin = useCallback(
    (value: FilterOrigin | ((current: FilterOrigin) => FilterOrigin)) =>
      updateMultiSelectFilter('origin', filterOriginDefault, value),
    [updateMultiSelectFilter]
  );

  const clearAllFilters = useCallback(() => {
    updateFilterParams(() => ({
      search: undefined,
      access: undefined,
      expires: undefined,
      role: undefined,
      origin: undefined
    }));
  }, [updateFilterParams]);

  const isAnyFilterEnabled = useMemo(
    () => hasEnabledFilter(filterStr, filterAccess, filterUserExpires, filterRole, filterOrigin),
    [filterStr, filterAccess, filterUserExpires, filterRole, filterOrigin]
  );

  const filters: UsersFilterState = useMemo(
    () => ({
      filter: {
        filterAccess,
        filterUserExpires,
        filterRole,
        filterOrigin
      },
      setters: {
        setFilterAccess,
        setFilterUserExpires,
        setFilterRole,
        setFilterOrigin
      },
      clearAllFilters,
      isAnyFilterEnabled
    }),
    [
      filterAccess,
      filterUserExpires,
      filterRole,
      filterOrigin,
      setFilterAccess,
      setFilterUserExpires,
      setFilterRole,
      setFilterOrigin,
      clearAllFilters,
      isAnyFilterEnabled
    ]
  );

  const searchedVisitors = useVisitorSearch({
    visitors: allVisitors,
    cards: allVisitorsCards,
    search: filterStr,
    translation
  });

  const filteredVisitors = useMemo(
    () =>
      filterVisitors({
        visitors: searchedVisitors,
        cards: allVisitorsCards,
        filterAccess,
        filterUserExpires,
        filterRole,
        filterOrigin
      }),
    [searchedVisitors, allVisitorsCards, filterAccess, filterUserExpires, filterRole, filterOrigin]
  );

  const countCaption = `${filteredVisitors.length} ${toLower(translation.of)} ${allVisitors.length} ${toLower(
    translation.users
  )}`;

  const sections = useMemo(() => createUserFilterSections({ translation, filters }), [translation, filters]);
  const chips = useMemo(() => createUserFilterChips({ translation, filters }), [translation, filters]);

  return {
    filterStr,
    setFilterStr,
    filteredVisitors,
    sections,
    chips,
    countCaption,
    isAnyFilterEnabled,
    clearAllFilters
  };
};
